import { useCallback, useEffect, useMemo } from 'react';

import {
  ValidationItems,
  Widget,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';

import { ERROR_PROPS, PROPS } from './constants';

export interface BankAccountHook {
  sortCodeValidation: ValidationItems;
  timeValidation: ValidationItems;
  error?: string;
  disabled: boolean;
}

export default function useBankAccount(): BankAccountHook {
  const { setValue, formState } = useFormContext();

  const [years, months, confirmation, noIva] = useWatch({
    name: [
      PROPS.accountYearsOwned.name,
      PROPS.accountMonthsOwned.name,
      PROPS.confirmAccount.name,
      PROPS.iva.name,
    ],
  });

  const validatePattern = useCallback(
    (sortcode?: string | number | boolean) => {
      if (!sortcode || typeof sortcode !== 'string') return false;
      const formatted = sortcode?.replace(/undefined/g, '');
      return /^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(formatted);
    },
    [],
  );

  const validateLength = useCallback((sortcode?: string | number | boolean) => {
    if (!sortcode || typeof sortcode !== 'string') return false;
    return sortcode?.replace(/-/g, '')?.replace(/undefined/g, '')?.length === 6;
  }, []);

  const sortCodeValidation = useMemo(
    () => ({
      validate: {
        value: {
          length: (value?: string | number | boolean) => validateLength(value),
          pattern: (value?: string | number | boolean) =>
            validatePattern(value),
        },
        message: {
          length: 'Sort code must be 6 digits',
          pattern: 'You must enter a valid sort code',
        },
      },
      required: {
        value: true,
        message: 'You must provide the your sort code',
      },
    }),
    [validatePattern, validateLength],
  );

  const timeValidation = useMemo(
    () => ({
      ...Widget.CreditBankAccountMonthsHeldValidation,
      required: {
        value: true,
        message:
          'You must provide the amount of time you have held this bank account for',
      },
    }),
    [],
  );

  const error = useMemo(() => {
    const heldError = (formState?.errors?.credit as StringIndexedObject)
      ?.bank_account?.months_held;
    if (!heldError) return undefined;
    if (heldError?.type === 'required') return ERROR_PROPS.label;
    return 'There is an error with this input';
  }, [formState]);

  const disabled = useMemo(
    () => !noIva || !confirmation,
    [noIva, confirmation],
  );

  useEffect(() => {
    if (!years && !months) {
      setValue('credit.bank_account.months_held', undefined);
      return;
    }
    const time = (years || 0) * 12 + (months || 0);
    setValue('credit.bank_account.months_held', time);
  }, [years, months, setValue]);

  useEffect(() => {
    if (!noIva) {
      setValue(
        'credit.finance_details.bankrupcy_or_iva_in_last_5_years',
        'yes',
      );
      return;
    }
    setValue('credit.finance_details.bankrupcy_or_iva_in_last_5_years', 'no');
  }, [noIva, setValue]);

  return {
    sortCodeValidation,
    timeValidation,
    error,
    disabled,
  };
}
